<template>
  <EBaseButton
    v-bind="$attrs"
    :size="size"
    :class="buttonClasses"
    class="button"
    v-on="$listeners"
  >
    <span
      v-if="iconPosition === 'end'"
      data-test-id="button-text"
      class="button__text"
    >
      <slot />
    </span>
    <EIcon
      v-if="icon"
      :icon="icon"
      :size="computedIconSize"
      class="button__icon"
      data-test-id="button-icon"
      :class="{ ...iconClasses, 'button__icon--with-text': Boolean($slots.default) }"
    />
    <span
      v-if="iconPosition === 'start'"
      data-test-id="button-text"
      class="button__text"
    >
      <slot />
    </span>
  </EBaseButton>
</template>

<script>
import classes from '~~/common/vue/mixins/classes';

import EIcon from '~~/common/vue/components/icon';
import EBaseButton from './base';

export const iconPositions = ['start', 'end'];
export const iconSizeDefaults = {
  xs: 's',
  s: 's',
  m: 's',
  l: 'm',
};

export default {
  name: 'EButton',
  components: {
    EBaseButton,
    EIcon,
  },
  mixins: [classes],
  props: {
    icon: {
      type: String,
      default: '',
    },
    iconSize: {
      type: String,
      default: '',
    },
    /**
     * one of `start`, `end`
     */
    iconPosition: {
      type: String,
      default: 'start',
      cssClass: true,
      validator: (position) => iconPositions.includes(position),
    },
    /**
     * Whether text should be move below icon on mobile
     */
    mobileStack: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
    size: {
      type: String,
      default: 'm',
    },
  },
  computed: {
    buttonClasses() {
      return this.classes.concat(
        this.icon && !this.$slots.default ? 'button-icon' : [],
      );
    },
    iconClasses() {
      const prefix = 'button__icon';
      return {
        [`${prefix}--icon-${this.icon}`]: true,
        [`${prefix}--size-${this.iconSize}`]: true,
      };
    },
    computedIconSize() {
      return this.iconSize || iconSizeDefaults[this.size] || 's';
    },
  },
};
</script>

<style lang="scss" scoped>
.button__text {
  display: inline-flex;
  align-items: center;
  font-weight: $font-weight-l;
  justify-content: center;
}

.button__icon {
  display: block;
  // ignore any padding if only icon is visible
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.button__icon--with-text {
  // the icon must now take up space
  position: static;
  transform: none;

  .button--icon-position-start & {
    margin-right: $space-2s;
  }

  .button--icon-position-end & {
    margin-left: $space-2s;
  }
}

.button--mobile-stack {
  @include tablet-down {
    flex-direction: column;
    padding: $space-2s 0;

    .button__icon {
      margin-right: 0;
      margin-left: 0;
    }

    .button__text {
      padding-top: $space-2s;
    }
  }
}
</style>
